import { Box, Grid, Typography, useTheme, styled } from "@mui/material";
import { Observer, observer } from "mobx-react";
import React, { useRef } from "react";
import appTheme from "Theme/AppTheme";
import AcxButton from "components/UI/AcxButton";
import { useStore } from "utils/useStore";
import { ClassifierBuilderV2Store } from "../../Stores/ClassifierBuilderV2Store";
import ConversationHighlights from "components/Conversations/Views/Main/Highlights/ConversationHighlights";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import { ConversationData } from "services/ConversationService";
import { parseFromISO, standardDateFormat } from "utils/DateTimeUtils";

const StyledConvoItemContainer = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    borderBottom: "0.5px solid var(--neutral-100, #E5E7EB)",
}));

const StyledGoToConvoBtn = styled(AcxButton)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: "white",
    height: "100%",
    margin: "0px",
    color: appTheme.palette.blackFont.main,
    border: "1px solid #E4E4E7",
    padding: "4px 10px",
    width: "100%",
    minWidth: "150px",
    maxWidth: "150px",
    '&:hover': {
        color: "white",
    },
    [theme.breakpoints.down(1300)]: {
        textWrap: "wrap",
        minWidth: "100px",
    },
}));

const StyledSummaryTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
}));

const StyledConvoDetailContainer = styled(Box)(({ theme }) => ({
    maxWidth: "90%",
}));

type ConversationsRbcResultsListProps = {};

const ConversationsRbcResultsList: React.FC<ConversationsRbcResultsListProps> = observer(() => {
    const topOfList = useRef<HTMLDivElement>(null);
    const store = useStore(ClassifierBuilderV2Store);
    const theme = useTheme();

    React.useEffect(() => {
        if (topOfList.current) {
            topOfList.current.scrollIntoView({ behavior: "smooth" });
            const scrollPosition =
                window.scrollY || document.documentElement.scrollTop;
            window.scrollTo(0, scrollPosition - 20);
        }
    }, [store.pageNumber]);

    const RbcConversationItem: React.FC<{
        res: ConversationData;
        index: number;
    }> = observer((props) => {
        const itemNumber: number =
            (store.pageNumber - 1) * 10 + (props.index + 1);

        const date = standardDateFormat(
            parseFromISO(props.res.timestamp, false),
            "MM/DD/YYYY",
        );

        return (
            <StyledConvoItemContainer
                item
                container
                direction="column"
                className="pendo-ignore"
            >
                <Grid
                    item
                    container
                    direction="row"
                    wrap="nowrap"
                    justifyContent="flex-start"
                    sx={{ paddingBottom: theme.spacing(1) }}
                >
                    <Typography
                        style={{
                            color: "#2C3F92",
                            fontWeight: "bold",
                            marginRight: "8px",
                        }}
                    >
                        #{itemNumber} {" "}
                    </Typography>
                    <Typography style={{ color: "#9CA3AF" }}>
                        {durationDisplay(props.res.callDurationMillis / 1000)} | {date}
                    </Typography>
                </Grid>

                <Grid item container direction="row">
                    <Grid item xs={9}>
                        <StyledConvoDetailContainer className="pendo-ignore">
                            <AcxExpandableText pendoIgnore>
                                <StyledSummaryTitle as="span">
                                    Summary: {" "}
                                </StyledSummaryTitle>
                                {props.res.summary}
                            </AcxExpandableText>
                            {props.res.highlights && (
                                <Box marginTop={1}>
                                    <ConversationHighlights
                                        highlights={props.res.highlights}
                                        conversationClassifiers={
                                            props.res.conversationClassifiers
                                        }
                                        disableChips
                                        isChat={
                                            props.res.mediaType === 6 &&
                                            props.res.callDurationMillis === 0
                                        }
                                    />
                                </Box>
                            )}
                        </StyledConvoDetailContainer>
                    </Grid>
                    <Grid
                        item
                        container
                        justifyContent="flex-end"
                        alignItems="center"
                        xs={3}
                    >
                        <StyledGoToConvoBtn
                            fullWidth
                            color="primary"
                            onClick={() => {
                                window.open(
                                    `${window.location.origin}/app/conversations/${props.res.conversationId}`,
                                    "_blank",
                                );
                            }}
                            containerStyle={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            Go To Conversation
                        </StyledGoToConvoBtn>
                    </Grid>
                </Grid>
            </StyledConvoItemContainer>
        );
    });

    return (
        <Observer>
            {() => (
                <>
                    <div ref={topOfList}></div>
                    {store.rbcConversationResults.map((res, index) => (
                        <RbcConversationItem
                            key={`${res.conversationId}-${index}`}
                            res={res}
                            index={index}
                        />
                    ))}
                </>
            )}
        </Observer>
    );
});

export default ConversationsRbcResultsList;
